/**
 * Where somebody was trying to go before we made them sign in.
 *
 * WHY THIS EXISTS. Halo writes a link into every ticket note it syncs — that
 * is how a tech gets from a ticket back to the conversation. Clicking one
 * while signed out bounced to `/sign-in` and then landed on `/`, so the thread
 * they clicked was simply gone and they had to find it by hand. Which is the
 * common case, not the edge one: you are in Halo precisely because you do not
 * already have the console open.
 *
 * WHY SESSION STORAGE RATHER THAN ROUTER STATE. Entra sign-in leaves the page
 * entirely — the browser goes to Microsoft and comes back — so anything held
 * in React or in the router's own state is gone by the time it matters. This
 * has to survive a full navigation, and `sessionStorage` is scoped to the tab
 * and cleared when it closes, which is exactly the lifetime an in-progress
 * sign-in has.
 *
 * ONLY EVER A PATH ON THIS DEPLOYMENT. Everything stored here is read back
 * into a client-side navigation, so a value that could be an absolute URL
 * would be an open redirect wearing a helpful hat. `remember` refuses
 * anything that is not a same-origin path, and `take` re-checks on the way
 * out rather than trusting what it reads: sessionStorage is writable by any
 * script on this origin, so the value is untrusted input by the time it comes
 * back.
 */
const KEY = 'cc.intendedDestination'

/** A path on this deployment: starts with a single '/', and is not '//host' (protocol-relative). */
function isSafePath(value: string): boolean {
  return value.startsWith('/') && !value.startsWith('//')
}

/**
 * Remember where this person was heading. Ignores the sign-in page itself —
 * "return to the sign-in page after signing in" is a loop, not a destination.
 */
export function remember(pathWithQuery: string): void {
  try {
    if (!isSafePath(pathWithQuery)) return
    if (pathWithQuery.startsWith('/sign-in')) return
    window.sessionStorage.setItem(KEY, pathWithQuery)
  } catch {
    // Private browsing, or storage disabled. Losing the destination sends
    // somebody to the console's front page, which is the old behaviour — it
    // must never stop them signing in.
  }
}

/**
 * The remembered destination, consumed. Returns `null` when there is nothing
 * to go back to, which callers should read as "the front page".
 *
 * Consumed on read so a later sign-in does not replay a stale destination
 * from an hour ago.
 */
export function take(): string | null {
  try {
    const value = window.sessionStorage.getItem(KEY)
    window.sessionStorage.removeItem(KEY)
    return value !== null && isSafePath(value) ? value : null
  } catch {
    return null
  }
}
